import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import FinanceLayoutPage from './financePageLayout';
import { useQuery } from 'react-query';
import ExplorePage from './explorePage';
import { getAllClubs } from '../../../api/club';
import { getLeagues, getLeaguesHistory } from '../../../api/league';
import { getLicenseFinance,
         getStaffResponse,
         getPlayersCount,
         getLicenseFacilityExplore,
         getLicenseTeamsExplore,
         getPlayersExplore, } from '../../../api/licence';
import { setClubOptions, setFacilitiesResponse, setFinanceResponse, setLeagueOptions, setLeaguesHistory, setPlayersCountResponse, setPlayersResponse, setStaffResponse, setTeamsResponse } from '../../../redux/reducers/licenceAnalytics';

interface FinancePageProps {
  mode: string;
}

 export const useFinanceData = () => {
  const dispatch = useDispatch();

  // Data fetching
  const { data: clubs = [] }:any = useQuery(['clubs'], getAllClubs, { enabled: true });
  const { data: leagues = [] }:any = useQuery('leagues', getLeagues);
  const { data: financeData = [] } = useQuery('finance', getLicenseFinance);
  const { data: staffResponse = [] } = useQuery('staff', getStaffResponse);
  const { data: facilityData = [] } = useQuery('facility', getLicenseFacilityExplore);
  const { data: teamsData = [] } = useQuery('teams', getLicenseTeamsExplore);
  const { data: playersCount = [] } = useQuery('playersCount', getPlayersCount);
  const { data: playersResponse = [] } = useQuery('players', getPlayersExplore);
  const { data: leaguesHistory = [] }:any = useQuery('leaguesHistory', getLeaguesHistory);

  // Memoized filtered data
  const financeWithoutTraining = useMemo(() => financeData.filter((finance:any) => finance.club.leagueId !== 2011), [
    financeData,
  ]);
  const leagueWithoutTrainingAndJFL = useMemo(() => leagues.filter((league:any) => (league.id !== 2011 && league.id !== 2012)), [leagues]);
  const leaguesHistoryWithoutTraining = useMemo(() => 
    leaguesHistory.map((item: any) => ({
      ...item,
      leagues: item.leagues.filter((league:any) => league.leagueId !== 2011)
    }))
  , [leaguesHistory]);
  const clubsWithoutTraining = useMemo(() => clubs.filter((club:any) => club.leagueId !== 2011), [clubs]);
  const staffWithoutTraining = useMemo(() => staffResponse.filter((staff:any) => staff?.club?.league?.id !== 2011), [
    staffResponse,
  ]);
  const facilityWithoutTraining = useMemo(() => facilityData.filter((facility:any) => facility?.club?.league?.id != 2011), [
    facilityData,
  ]);
  const playersWithoutTraning = useMemo(() => playersResponse.filter((player:any) => player?.club?.league?.id != 2011), [
    playersResponse,
  ]);
  const teamsWithoutTraning = useMemo(() => teamsData.filter((team:any) => team?.club?.league?.id != 2011), [
    teamsData,
  ]);
  

  // Dispatch actions
  const dispatchFinance = useCallback(() => {
    dispatch(setFinanceResponse({ response: financeWithoutTraining }));
  }, [dispatch, financeWithoutTraining]);

  const dispatchLeagueOptions = useCallback(() => {
    dispatch(setLeagueOptions({ newValue: leagueWithoutTrainingAndJFL }));
  }, [dispatch, leagueWithoutTrainingAndJFL]);

  const dispatchLeaguesHistory = useCallback(() => {
    dispatch(setLeaguesHistory({ newValue: leaguesHistoryWithoutTraining }));
  }, [dispatch, leaguesHistoryWithoutTraining]);

  const dispatchFacilities = useCallback(() => {
    dispatch(setFacilitiesResponse({ response: facilityData }));
    dispatch(setTeamsResponse({ response: teamsWithoutTraning }));
    dispatch(setPlayersCountResponse({ response: playersCount }));
  }, [dispatch, facilityData, teamsWithoutTraning, playersCount]);

  const dispatchClubOptions = useCallback(() => {
    dispatch(setClubOptions({ newValue: clubsWithoutTraining }));
  }, [dispatch, clubsWithoutTraining]);

  const dispatchStaff = useCallback(() => {
    dispatch(setStaffResponse({ response: staffWithoutTraining }));
  }, [dispatch, staffWithoutTraining]);

  const dispatchfacilities = useCallback(() => {
    dispatch(setFacilitiesResponse({ response: facilityWithoutTraining }));
  }, [dispatch, facilityWithoutTraining]);

  const dispatchPlayers = useCallback(() => {
    dispatch(setPlayersResponse({ response: playersWithoutTraning }));
  }, [dispatch, playersWithoutTraning]);

  useEffect(() => {
    dispatchFinance();
  }, [dispatchFinance]);

  useEffect(() => {
    dispatchLeagueOptions();
  }, [dispatchLeagueOptions]);
  
  useEffect(() => {
    dispatchLeaguesHistory();
  }, [dispatchLeaguesHistory]);

  useEffect(() => {
    dispatchFacilities();
  }, [dispatchFacilities]);

  useEffect(() => {
    dispatchClubOptions();
  }, [dispatchClubOptions]);

  useEffect(() => {
    dispatchStaff();
  }, [dispatchStaff]);

  useEffect(() => {
    dispatchfacilities();
  }, [dispatchfacilities]);

  useEffect(() => {
    dispatchPlayers();
  }, [dispatchPlayers]);
};

const FinancePage: React.FC<FinancePageProps> = ({ mode }) => {
  useFinanceData();

  return <>{mode === 'Dashboard' ? <FinanceLayoutPage /> : <ExplorePage />}</>;
};

export default FinancePage;
